// src/pages/itsm/KnowledgeBase.tsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sun, Moon } from 'lucide-react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Pill, Btn, StatTile, EmptyState, ConfirmDialog, showToast, ToastHost } from '../../components/ui/UI'

const STATUS_CONFIG: Record<string, { label: string; tone: string }> = {
    draft: { label: 'Draft', tone: 'neutral' },
    published: { label: 'Published', tone: 'success' },
    archived: { label: 'Archived', tone: 'danger' },
}

export default function KnowledgeBase() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [categories, setCategories] = useState<any[]>([])
    const [articles, setArticles] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedCat, setSelectedCat] = useState<string>('all')
    const [search, setSearch] = useState('')
    const [showAdd, setShowAdd] = useState(false)
    const [readingArticle, setReadingArticle] = useState<any>(null)
    const [editingArticle, setEditingArticle] = useState<any>(null)
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

    const [form, setForm] = useState({
        title: '', content: '', category_id: '',
        status: 'draft', author: '', tags: '',
    })

    useEffect(() => { fetchAll() }, [selectedCat])

    const fetchAll = async () => {
        let query = supabase
            .from('itsm_kb_articles')
            .select('*, itsm_kb_categories(name, icon)')
            .order('created_at', { ascending: false })
        if (selectedCat !== 'all') query = query.eq('category_id', Number(selectedCat))

        const [{ data: a }, { data: cats }] = await Promise.all([
            query,
            supabase.from('itsm_kb_categories').select('*').order('sort_order'),
        ])
        setArticles(a || [])
        setCategories(cats || [])
        setLoading(false)
    }

    const saveArticle = async () => {
        const payload = {
            ...form,
            category_id: form.category_id ? Number(form.category_id) : null,
            tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        }
        if (editingArticle) {
            await supabase.from('itsm_kb_articles').update(payload).eq('id', editingArticle.id)
            setEditingArticle(null)
        } else {
            await supabase.from('itsm_kb_articles').insert([payload])
        }
        setForm({ title: '', content: '', category_id: '', status: 'draft', author: '', tags: '' })
        setShowAdd(false)
        fetchAll()
        showToast('Article saved', 'success')
    }

    const deleteArticle = async (id: number) => {
        await supabase.from('itsm_kb_articles').delete().eq('id', id)
        fetchAll()
        showToast('Article deleted', 'success')
    }

    const togglePublish = async (id: number, status: string) => {
        await supabase.from('itsm_kb_articles').update({ status: status === 'published' ? 'draft' : 'published' }).eq('id', id)
        fetchAll()
    }

    const markHelpful = async (id: number, helpful: boolean) => {
        const field = helpful ? 'helpful_yes' : 'helpful_no'
        const article = articles.find(a => a.id === id)
        if (!article) return
        await supabase.from('itsm_kb_articles').update({ [field]: (article[field] || 0) + 1 }).eq('id', id)
        fetchAll()
        if (readingArticle?.id === id) setReadingArticle({ ...readingArticle, [field]: (readingArticle[field] || 0) + 1 })
    }

    const incrementViews = async (article: any) => {
        await supabase.from('itsm_kb_articles').update({ views: (article.views || 0) + 1 }).eq('id', article.id)
        setReadingArticle(article)
    }

    const filtered = articles.filter(a =>
        a.title?.toLowerCase().includes(search.toLowerCase()) ||
        a.content?.toLowerCase().includes(search.toLowerCase()) ||
        a.tags?.some((t: string) => t.toLowerCase().includes(search.toLowerCase()))
    )

    const s = styles

    if (loading) return (
        <div className="jr-app" data-theme={theme} style={s.loading}>Loading...</div>
    )

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <ToastHost />
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>← Back to ITSM</button>

            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Knowledge Base</h1>
                    <p style={s.subtitle}>{articles.length} articles · {articles.filter(a => a.status === 'published').length} published</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                        onClick={toggleTheme}
                        aria-label="Toggle dark mode"
                        style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: `1px solid ${T.borderStrong}`, background: T.surface, color: T.text2, cursor: 'pointer' }}
                    >
                        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                    </button>
                    <Btn variant="primary" style={undefined} onClick={() => setShowAdd(true)}>+ New Article</Btn>
                </div>
            </div>

            {/* Stats */}
            <Card style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', padding: 0, marginBottom: 20 }}>
                <StatTile label="Total Articles" value={articles.length} tone="info" />
                <StatTile label="Published" value={articles.filter(a => a.status === 'published').length} tone="success" />
                <StatTile label="Total Views" value={articles.reduce((sum, a) => sum + (a.views || 0), 0)} tone="violet" />
                <StatTile label="Helpful Votes" value={articles.reduce((sum, a) => sum + (a.helpful_yes || 0), 0)} tone="warning" />
            </Card>

            <div style={s.layout}>
                {/* Sidebar */}
                <Card style={{ padding: 12, height: 'fit-content' }}>
                    <p style={s.sidebarTitle}>Categories</p>
                    <button
                        style={{ ...s.catBtn, background: selectedCat === 'all' ? T.accent : 'transparent', color: selectedCat === 'all' ? T.accentFg : T.text2 }}
                        onClick={() => setSelectedCat('all')}>
                        <span>📚</span>
                        <span style={{ flex: 1, textAlign: 'left' as const }}>All Articles</span>
                        <span style={{ fontSize: 11, opacity: 0.7 }}>{articles.length}</span>
                    </button>
                    {categories.map(cat => {
                        const count = articles.filter(a => a.category_id === cat.id).length
                        const isActive = selectedCat === String(cat.id)
                        return (
                            <button key={cat.id}
                                style={{ ...s.catBtn, background: isActive ? T.accent : 'transparent', color: isActive ? T.accentFg : T.text2 }}
                                onClick={() => setSelectedCat(String(cat.id))}>
                                <span>{cat.icon}</span>
                                <span style={{ flex: 1, textAlign: 'left' as const }}>{cat.name}</span>
                                <span style={{ fontSize: 11, opacity: 0.7 }}>{count}</span>
                            </button>
                        )
                    })}
                </Card>

                {/* Articles */}
                <div style={{ flex: 1 }}>
                    <input style={{ ...s.search, marginBottom: 16 }} placeholder="Search articles..." value={search} onChange={e => setSearch(e.target.value)} />

                    {filtered.length === 0 ? (
                        <Card>
                            <EmptyState icon="📚" title="No articles found" />
                        </Card>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {filtered.map(article => {
                                const status = STATUS_CONFIG[article.status] || STATUS_CONFIG.draft
                                return (
                                    <Card key={article.id}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => incrementViews(article)}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                    <span style={{ fontSize: 16 }}>{article.itsm_kb_categories?.icon || '📄'}</span>
                                                    <span style={{ fontSize: 11, color: T.text3, fontWeight: 600 }}>{article.itsm_kb_categories?.name}</span>
                                                    <Pill tone={status.tone as any}>{status.label}</Pill>
                                                </div>
                                                <p style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>{article.title}</p>
                                                <p style={{ fontSize: 12, color: T.text2, lineHeight: 1.5 }}>
                                                    {article.content?.substring(0, 120)}...
                                                </p>
                                                <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                                                    <span style={{ fontSize: 11, color: T.text3 }}>👁 {article.views || 0} views</span>
                                                    <span style={{ fontSize: 11, color: T.success }}>👍 {article.helpful_yes || 0}</span>
                                                    <span style={{ fontSize: 11, color: T.danger }}>👎 {article.helpful_no || 0}</span>
                                                    {article.author && <span style={{ fontSize: 11, color: T.text3 }}>✍️ {article.author}</span>}
                                                </div>
                                                {article.tags?.length > 0 && (
                                                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' as const }}>
                                                        {article.tags.map((tag: string) => (
                                                            <Pill key={tag} tone="info">#{tag}</Pill>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: 6, marginLeft: 12, flexShrink: 0 }}>
                                                <Btn size="sm" style={undefined} onClick={() => togglePublish(article.id, article.status)}>
                                                    {article.status === 'published' ? 'Unpublish' : 'Publish'}
                                                </Btn>
                                                <Btn size="sm" style={undefined} onClick={() => {
                                                    setEditingArticle(article)
                                                    setForm({ title: article.title, content: article.content, category_id: article.category_id || '', status: article.status, author: article.author || '', tags: article.tags?.join(', ') || '' })
                                                    setShowAdd(true)
                                                }}>Edit</Btn>
                                                <Btn size="sm" style={{ color: T.danger }} onClick={() => setConfirmDeleteId(article.id)}>Delete</Btn>
                                            </div>
                                        </div>
                                    </Card>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Read Article Modal */}
            {readingArticle && (
                <div style={s.overlay} onClick={() => setReadingArticle(null)}>
                    <div style={{ ...s.modal, maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                            <div>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                    <span style={{ fontSize: 11, color: T.text3, fontWeight: 600 }}>{readingArticle.itsm_kb_categories?.name}</span>
                                </div>
                                <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{readingArticle.title}</h2>
                            </div>
                            <button onClick={() => setReadingArticle(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.text3 }}>✕</button>
                        </div>
                        <div style={{ fontSize: 14, color: T.text2, lineHeight: 1.8, whiteSpace: 'pre-wrap', marginBottom: 24 }}>
                            {readingArticle.content}
                        </div>
                        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <p style={{ fontSize: 13, color: T.text2 }}>Was this article helpful?</p>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button style={{ height: 34, padding: '0 16px', background: T.successBg, color: T.success, border: `1px solid ${T.successBorder}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                                    onClick={() => markHelpful(readingArticle.id, true)}>
                                    👍 Yes ({readingArticle.helpful_yes || 0})
                                </button>
                                <button style={{ height: 34, padding: '0 16px', background: T.dangerBg, color: T.danger, border: `1px solid ${T.dangerBorder}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                                    onClick={() => markHelpful(readingArticle.id, false)}>
                                    👎 No ({readingArticle.helpful_no || 0})
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            {showAdd && (
                <div style={s.overlay}>
                    <div style={{ ...s.modal, maxWidth: 620, maxHeight: '90vh', overflowY: 'auto' }}>
                        <h2 style={s.modalTitle}>{editingArticle ? 'Edit Article' : 'New Article'}</h2>
                        <div style={s.field}>
                            <label style={s.label}>Title *</label>
                            <input style={s.input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="How to reset your password..." />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div style={s.field}>
                                <label style={s.label}>Category</label>
                                <select style={s.input} value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                                    <option value="">Select category...</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Status</label>
                                <select style={s.input} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                    <option value="draft">Draft</option>
                                    <option value="published">Published</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Author</label>
                            <input style={s.input} value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} placeholder="Agent name..." />
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Tags (comma separated)</label>
                            <input style={s.input} value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="password, reset, account..." />
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Content *</label>
                            <textarea
                                style={{ ...s.input, height: 200, padding: '8px 10px', resize: 'vertical' as const }}
                                placeholder="Write your article content here..."
                                value={form.content}
                                onChange={e => setForm({ ...form, content: e.target.value })}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Btn variant="ghost" style={{ flex: 1, justifyContent: 'center', border: `1px solid ${T.borderStrong}` }} onClick={() => { setShowAdd(false); setEditingArticle(null) }}>Cancel</Btn>
                            <Btn variant="primary" style={undefined} onClick={saveArticle}>
                                {editingArticle ? 'Save Changes' : 'Create Article'}
                            </Btn>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirmDeleteId !== null}
                onClose={() => setConfirmDeleteId(null)}
                onConfirm={() => { if (confirmDeleteId !== null) deleteArticle(confirmDeleteId) }}
                title="Delete article?"
                message="This action cannot be undone."
                confirmLabel="Delete"
                danger
            />
        </div>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1200, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    back: { background: 'none', border: 'none', color: T.info, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    subtitle: { fontSize: 13, color: T.text2 },
    layout: { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20 },
    sidebarTitle: { fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, padding: '0 8px' },
    catBtn: { width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginBottom: 2 },
    search: { width: '100%', height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 12px', fontSize: 13, outline: 'none', background: T.surface, color: T.text },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
    modal: { background: T.surface, borderRadius: 12, padding: 28, width: '100%' },
    modalTitle: { fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20 },
    field: { marginBottom: 14 },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 5 },
    input: { width: '100%', height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, color: T.text, outline: 'none', background: T.surface },
}
